/**
 * The states that are not "here is your recording".
 *
 * These matter more than the happy path, because they are the common case: a
 * call that ended thirty seconds ago has no transcript, no metrics and no
 * playback track, and will not have them for minutes. If that renders as an
 * error — or worse, as an empty page — the product looks broken at exactly the
 * moment a new user first looks at it.
 *
 * So every one of these says three things: what state this session is in, why
 * that is normal (or not), and what happens next without the user doing
 * anything.
 */

import type * as React from 'react'
import { Archive, Ban, CircleAlert, Hourglass, Radio, RefreshCw, X } from 'lucide-react'
import type { SessionDetailView } from '@era/shared'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'

/* -------------------------------------------------------------------------- */
/* the waiting room                                                            */
/* -------------------------------------------------------------------------- */

type Waiting = {
  icon: React.ReactNode
  title: string
  body: string
  /** False when nothing will ever change — recording was off, or retention ran. */
  pending: boolean
  tone: 'accent' | 'warn' | 'critical' | 'neutral'
}

export function describeWaiting(view: SessionDetailView): Waiting {
  if (view.recording_ever_enabled === false) {
    return {
      icon: <Ban />,
      title: 'This call was never recorded',
      body: 'Audio capture was never enabled. There are no stored audio windows, so no transcript or conversation metrics will appear.',
      pending: false,
      tone: 'neutral',
    }
  }

  if (view.state === 'purged') {
    return {
      icon: <Archive />,
      title: 'The recordings have been deleted',
      body: 'Retention has run on this session. The call still happened and this page still knows who was on it and how long it lasted — the audio and the transcript are gone.',
      pending: false,
      tone: 'neutral',
    }
  }

  if (view.state === 'failed' || view.pipeline.state === 'failed') {
    return {
      icon: <CircleAlert />,
      title: 'The audio pipeline failed',
      body: `The recording was made but could not be processed${
        view.pipeline.outcome ? ` (${view.pipeline.outcome})` : ''
      }. This is the one state on this screen that is a real fault rather than a wait.`,
      pending: false,
      tone: 'critical',
    }
  }

  if (view.state === 'live' || view.state === 'pending') {
    return {
      icon: <Radio />,
      title: 'This call is still running',
      body: 'Recording happens per speaker as they talk, and processing starts when the last person leaves. Nothing to read yet.',
      pending: true,
      tone: 'accent',
    }
  }

  return {
    icon: <Hourglass />,
    title: 'Still being transcribed',
    body: 'The call is over and the audio is being turned into a transcript. This takes a few minutes and runs on its own — this page checks again by itself.',
    pending: true,
    tone: 'accent',
  }
}

export function WaitingCard({
  view,
  onRefresh,
  refreshing,
}: {
  view: SessionDetailView
  onRefresh: () => void
  refreshing: boolean
}) {
  const waiting = describeWaiting(view)
  const expected = view.pipeline.expected_sources ?? 0
  const analyzed = view.pipeline.analyzed_sources ?? 0

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-start sm:gap-5 sm:px-6 sm:py-7">
        <span
          className={cn(
            'flex size-11 shrink-0 items-center justify-center rounded-full [&_svg]:size-5',
            waiting.tone === 'accent' && 'bg-accent-soft text-accent',
            waiting.tone === 'warn' && 'bg-warn-soft text-warn',
            waiting.tone === 'critical' && 'bg-critical-soft text-critical',
            waiting.tone === 'neutral' && 'bg-surface-2 text-ink-muted',
          )}
        >
          {waiting.icon}
        </span>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-1.5">
            <h2 className="text-base font-semibold text-ink">{waiting.title}</h2>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-muted">{waiting.body}</p>
          </div>

          {waiting.pending && expected > 0 ? (
            <div className="max-w-sm space-y-1.5">
              <div className="flex items-baseline justify-between text-xs text-ink-muted">
                <span>
                  {analyzed} of {expected} speaker {expected === 1 ? 'track' : 'tracks'} processed
                </span>
                <span className="tabular">{Math.round((analyzed / expected) * 100)}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-sm bg-surface-2">
                <span
                  className="block h-full bg-accent"
                  style={{
                    width: `${Math.min(100, (analyzed / expected) * 100)}%`,
                    borderRadius: '0 4px 4px 0',
                  }}
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-dim">
            <span>
              pipeline <span className="ident">{view.pipeline.state ?? 'unknown'}</span>
              {view.pipeline.outcome && view.pipeline.outcome !== 'unknown'
                ? ` · ${view.pipeline.outcome}`
                : ''}
            </span>
            {view.fetched_at ? <span>checked {formatRelative(view.fetched_at)}</span> : null}
            {waiting.pending ? (
              <button
                type="button"
                onClick={onRefresh}
                className="inline-flex items-center gap-1.5 font-medium text-accent hover:underline"
              >
                <RefreshCw className={cn('size-3', refreshing && 'animate-spin')} />
                refresh
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* banners                                                                     */
/* -------------------------------------------------------------------------- */

export function Notice({
  tone,
  title,
  children,
  onDismiss,
}: {
  tone: 'warn' | 'critical' | 'neutral' | 'ok'
  title: string
  children?: React.ReactNode
  /**
   * Present only on a notice that reports something that HAPPENED rather than
   * a condition that persists.
   *
   * It is dismissed by hand and never on a timer. This banner sits above the
   * pinned player, so a self-clearing one would move the player under the
   * reader's thumb some seconds after they had stopped reading it — the exact
   * class of unrequested layout shift the rest of this screen is built to
   * avoid. A tap is a shift the reader asked for.
   */
  onDismiss?: () => void
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        tone === 'warn' && 'border-warn/30 bg-warn-soft/60',
        tone === 'critical' && 'border-critical/30 bg-critical-soft/60',
        tone === 'neutral' && 'border-line bg-surface-2/60',
        tone === 'ok' && 'border-ok/25 bg-ok-soft/60',
      )}
    >
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-[0.8125rem] font-semibold',
            tone === 'warn' && 'text-warn',
            tone === 'critical' && 'text-critical',
            tone === 'neutral' && 'text-ink',
            tone === 'ok' && 'text-ok',
          )}
        >
          {title}
        </p>
        {children ? (
          <p className="mt-1 text-[0.8125rem] leading-relaxed text-ink-muted">{children}</p>
        ) : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          // `-m-2 p-2` puts a 44px touch box around a 16px glyph without
          // adding 28px to the height of the banner it sits in.
          className="tap44 -m-2 shrink-0 rounded-md p-2 text-ink-dim transition-colors hover:text-ink"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  )
}

/**
 * The in-tab version of "not yet".
 *
 * Quieter than `WaitingCard` on purpose: by the time a reader is inside a tab
 * they have already been told at the top of the page that the pipeline is
 * running, and repeating it at full size reads as an error appearing twice.
 */
export function PendingBlock({
  title,
  reason,
  icon = <Hourglass />,
}: {
  title: string
  reason: string
  icon?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-line bg-surface-2/40 px-6 py-12 text-center">
      <span className="flex size-9 items-center justify-center rounded-full bg-surface text-ink-muted [&_svg]:size-4">
        {icon}
      </span>
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="max-w-md text-[0.8125rem] leading-relaxed text-ink-muted">{reason}</p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* loading and failure                                                         */
/* -------------------------------------------------------------------------- */

export function SessionSkeleton() {
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-96 max-w-full" />
        <Skeleton className="h-4 w-72" />
      </div>
      <Card className="px-6 py-5">
        <div className="flex items-center gap-5">
          <Skeleton className="size-12 rounded-full" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-3 w-full rounded-full" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </Card>
      <div className="grid gap-8 lg:grid-cols-[248px_1fr]">
        <div className="space-y-3">
          <Skeleton className="h-14 w-full rounded-lg" />
          <Skeleton className="h-14 w-full rounded-lg" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    </div>
  )
}

export function SessionFailure({
  sessionId,
  status,
  message,
}: {
  sessionId: string
  status: number
  message: string
}) {
  const missing = status === 404
  return (
    <div className="mx-auto max-w-xl space-y-4 py-16 text-center">
      <p className="eyebrow">{missing ? 'not found' : `error ${status || 'offline'}`}</p>
      <h1 className="display text-3xl text-ink">
        {missing ? 'No session with that id' : 'This session could not be loaded'}
      </h1>
      <p className="text-sm leading-relaxed text-ink-muted">
        {missing
          ? 'The control plane does not know it and this app never created it. A session id looks like s_20260808152625_xtAK7TGEoP.'
          : message}
      </p>
      <p className="ident text-xs text-ink-dim">{sessionId}</p>
    </div>
  )
}
